<template>
    <div :class="{ 'h-full': success, 'form-light' : !contrast }">
        <div v-if="success || errorMessage"
             class="relative bg-white rounded-2xl border-2 min-h-38 px-5 pt-13 pb-7 -mx-5 mt-20 mb-12 lg:mt-15 lg:pl-18 lg:pr-8 lg:py-9 lg:mr-0 lg:ml-13"
             :class="errorMessage ? 'border-red' : 'border-green'">
            <div class="text-white absolute left-1/2 -top-7 -translate-x-1/2 flex justify-center items-center w-14 h-14 transform rotate-45 rounded-mid
                                lg:-left-11 lg:top-1/2 lg:w-22 lg:h-22 lg:translate-x-0 lg:-translate-y-1/2 lg:rounded-3xl"
                 :class="errorMessage ? 'bg-red' : 'bg-green'">
                <icon :name="errorMessage ? 'close' : 'check'" class="fill-current w-6 h-6 transform -rotate-45" />
            </div>
            <slot v-if="errorMessage" name="error"/>
            <slot v-else name="thankyou"/>
        </div>
        <form v-else>
            <slot/>
            <div class="flex flex-col mt-12">
                <div :class="checkboxClasses">
                    <div class="form-check">
                        <input type="checkbox" name="privacy-notice" required id="form-check" :checked="checked"
                               @input="toggle">
                        <label for="form-check">
                            <slot name="form-check"/>
                        </label>
                        <span class="form-error" :class="{ '!text-white' : contrast }">{{ requiredErrorLabel }}</span>
                    </div>
                </div>
                <div class="flex gap-4 mt-8 lg:mt-14">
                    <loading-spinner v-show="sending"
                                     class="self-center w-10 h-10 text-fruechtebox-orange fill-current order-2 md:order-1"/>
                    <div class="group flex lg:justify-end cursor-pointe">
                        <button class="button"
                                :class="contrast ? 'group-hover:bg-light-red' : 'group-hover:bg-dark-red'"
                                type="submit"
                                id="submit-form"
                                @click.prevent="submit">
                            {{ submitLabel }}
                        </button>
                        <button class="button py-3 -ml-px"
                                :class="contrast ? 'group-hover:bg-light-red' : 'group-hover:bg-dark-red'"
                                type="submit"
                                @click.prevent="submit">
                            <icon name="mail" class="stroke-current w-4 h-4 mx-px lg:group-hover:animate-bounce"/>
                        </button>
                    </div>
                </div>
            </div>
        </form>
    </div>
</template>

<script lang="ts">
import Vue from 'vue';
import { Component, Prop, Provide } from 'vue-property-decorator';
import axios from 'axios';
import LoadingSpinner from '../base/LoadingSpinner.vue';

const FORM_ENDPOINT = '/.rest/api/v1/form';
const FORM_ENDPOINT_HR = '/.rest/api/v2/form/hr';
const FORM_ENDPOINT_FRUECHTEBOX_ORDER = '/.rest/api/v2/order';

interface FruitBoxLabels {
    label: string;
    weights: FruitBoxLabel[];
}

interface FruitBoxLabel {
    label: string;
}

@Component({
    components: {
        LoadingSpinner
    }
})
/* eslint @typescript-eslint/ban-ts-comment: "off" */
export default class FormSubmit extends Vue {
    @Prop({ required: true }) serverError: string;
    @Prop({ required: true }) successMessage: string;
    @Prop({ required: true }) submitLabel: string;
    @Prop({ default: 'checkbox' }) checkboxClasses: string;
    @Prop({ type: Boolean, default: false }) isHrForm: boolean;
    @Prop({ type: Boolean, default: false }) isFruechteboxOrderForm: boolean;
    @Prop({ default: () => [] }) fruitboxLabels: FruitBoxLabels[];
    @Prop({ default: () => [] }) fruitboxSubscriptionLabels: FruitBoxLabel[];
    @Prop({ type: Boolean, default: false }) contrast: boolean;
    @Prop({ required: true }) requiredErrorLabel: string;
    @Provide('register') childRegistration = this.registerItem;

    items = [];
    sending = false;
    success = false;
    checked = false;
    errorMessage = '';

    async submit({ target }) {
        this.errorMessage = '';
        const form: HTMLFormElement = target.form;
        const fd: FormData = new FormData(form);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const encodedFd: URLSearchParams = new URLSearchParams(fd as any); // this works but type checking fails without `any`

        try {
            if (form.checkValidity() && this.checkItemsValidity()) {
                this.sending = true;
                const endpoint = this.isFruechteboxOrderForm ? `${this.$contextPath}${FORM_ENDPOINT_FRUECHTEBOX_ORDER}/${this.$lang}` : (this.isHrForm ? `${this.$contextPath}${FORM_ENDPOINT_HR}/${this.$site}/${this.$lang}` : `${this.$contextPath}${FORM_ENDPOINT}/${this.$site}`);
                const res = await axios({
                    method: 'post',
                    url: endpoint,
                    headers: {
                        'Content-Type': 'application/x-www-form-urlencoded'
                    },
                    data: encodedFd
                });
                // send form submit event to gtm
                if (window.dataLayer) {
                    const dataLayer = window.dataLayer || [];
                    if (this.isFruechteboxOrderForm) {
                        const boxType = [];
                        const boxWeight = [];
                        const boxPrice = [];
                        const boxCount = [];
                        const boxFrequency = [];
                        // @ts-ignore
                        if ((fd.get('twoPackAmount') ?? 0) > 0) {
                            boxType.push(this.fruitboxLabels[0].label);
                            boxCount.push(fd.get('twoPackAmount'));
                            const weightOption = fd.get('twoPackWeightOption');
                            // @ts-ignore
                            if ((weightOption ?? 0) > 0 && typeof weightOption === 'string') {
                                const weightOptionNumber: number = parseInt(weightOption) - 1;
                                const weightOptionLabel = this.fruitboxLabels[0].weights[weightOptionNumber].label;
                                if (weightOptionLabel.includes('|')) {
                                    const weightLabelSplit = weightOptionLabel.split('|');
                                    boxWeight.push(weightLabelSplit[0].trim().split(' ')[0]);
                                    if (weightLabelSplit.length > 1) {
                                        boxPrice.push(weightLabelSplit[1].trim().split(' ')[0]);
                                    }
                                } else {
                                    boxPrice.push(this.fruitboxLabels[0].weights[weightOptionNumber].label);
                                    boxWeight.push(this.fruitboxLabels[0].weights[weightOptionNumber].label);
                                }
                            } else {
                                boxPrice.push('-1');
                                boxWeight.push('-1');
                            }
                            const subscriptionOption = fd.get('twoPackSubscriptionFrequencyOption');
                            // @ts-ignore
                            if ((subscriptionOption ?? 0) > 0 && typeof subscriptionOption === 'string') {
                                const subscriptionOptionNumber: number = parseInt(subscriptionOption) - 1;
                                const subscriptionOptionLabel = this.fruitboxSubscriptionLabels[subscriptionOptionNumber].label;
                                boxFrequency.push(subscriptionOptionLabel);
                            } else {
                                boxFrequency.push('-1');
                            }
                        }
                        // @ts-ignore
                        if ((fd.get('fivePackAmount') ?? 0) > 0) {
                            boxType.push(this.fruitboxLabels[1].label);
                            boxCount.push(fd.get('fivePackAmount'));
                            const weightOption = fd.get('fivePackWeightOption');
                            // @ts-ignore
                            if ((weightOption ?? 0) > 0 && typeof weightOption === 'string') {
                                const weightOptionNumber: number = parseInt(weightOption) - 1;
                                const weightOptionLabel = this.fruitboxLabels[1].weights[weightOptionNumber].label;
                                if (weightOptionLabel.includes('|')) {
                                    const weightLabelSplit = weightOptionLabel.split('|');
                                    boxWeight.push(weightLabelSplit[0].trim().split(' ')[0]);
                                    if (weightLabelSplit.length > 1) {
                                        boxPrice.push(weightLabelSplit[1].trim().split(' ')[0]);
                                    }
                                } else {
                                    boxPrice.push(this.fruitboxLabels[1].weights[weightOptionNumber].label);
                                    boxWeight.push(this.fruitboxLabels[1].weights[weightOptionNumber].label);
                                }
                            } else {
                                boxPrice.push('-1');
                                boxWeight.push('-1');
                            }
                            const subscriptionOption = fd.get('fivePackSubscriptionFrequencyOption');
                            // @ts-ignore
                            if ((subscriptionOption ?? 0) > 0 && typeof subscriptionOption === 'string') {
                                const subscriptionOptionNumber: number = parseInt(subscriptionOption) - 1;
                                const subscriptionOptionLabel = this.fruitboxSubscriptionLabels[subscriptionOptionNumber].label;
                                boxFrequency.push(subscriptionOptionLabel);
                            } else {
                                boxFrequency.push('-1');
                            }
                        }
                        // @ts-ignore
                        if ((fd.get('eightPackAmount') ?? 0) > 0) {
                            boxType.push(this.fruitboxLabels[2].label);
                            boxCount.push(fd.get('eightPackAmount'));
                            const weightOption = fd.get('eightPackWeightOption');
                            // @ts-ignore
                            if ((weightOption ?? 0) > 0 && typeof weightOption === 'string') {
                                const weightOptionNumber: number = parseInt(weightOption) - 1;
                                const weightOptionLabel = this.fruitboxLabels[2].weights[weightOptionNumber].label;
                                if (weightOptionLabel.includes('|')) {
                                    const weightLabelSplit = weightOptionLabel.split('|');
                                    boxWeight.push(weightLabelSplit[0].trim().split(' ')[0]);
                                    if (weightLabelSplit.length > 1) {
                                        boxPrice.push(weightLabelSplit[1].trim().split(' ')[0]);
                                    }
                                } else {
                                    boxPrice.push(this.fruitboxLabels[2].weights[weightOptionNumber].label);
                                    boxWeight.push(this.fruitboxLabels[2].weights[weightOptionNumber].label);
                                }
                            } else {
                                boxPrice.push('-1');
                                boxWeight.push('-1');
                            }
                            const subscriptionOption = fd.get('eightPackSubscriptionFrequencyOption');
                            // @ts-ignore
                            if ((subscriptionOption ?? 0) > 0 && typeof subscriptionOption === 'string') {
                                const subscriptionOptionNumber: number = parseInt(subscriptionOption) - 1;
                                const subscriptionOptionLabel = this.fruitboxSubscriptionLabels[subscriptionOptionNumber].label;
                                boxFrequency.push(subscriptionOptionLabel);
                            } else {
                                boxFrequency.push('-1');
                            }
                        }
                        // @ts-ignore
                        if ((fd.get('exoticPackAmount') ?? 0) > 0) {
                            boxType.push(this.fruitboxLabels[3].label);
                            boxCount.push(fd.get('exoticPackAmount'));
                            const weightOption = fd.get('exoticPackWeightOption');
                            // @ts-ignore
                            if ((weightOption ?? 0) > 0 && typeof weightOption === 'string') {
                                const weightOptionNumber: number = parseInt(weightOption) - 1;
                                const weightOptionLabel = this.fruitboxLabels[3].weights[weightOptionNumber].label;
                                if (weightOptionLabel.includes('|')) {
                                    const weightLabelSplit = weightOptionLabel.split('|');
                                    boxWeight.push(weightLabelSplit[0].trim().split(' ')[0]);
                                    if (weightLabelSplit.length > 1) {
                                        boxPrice.push(weightLabelSplit[1].trim().split(' ')[0]);
                                    }
                                } else {
                                    boxPrice.push(this.fruitboxLabels[3].weights[weightOptionNumber].label);
                                    boxWeight.push(this.fruitboxLabels[3].weights[weightOptionNumber].label);
                                }
                            } else {
                                boxPrice.push('-1');
                                boxWeight.push('-1');
                            }
                            const subscriptionOption = fd.get('exoticPackSubscriptionFrequencyOption');
                            // @ts-ignore
                            if ((subscriptionOption ?? 0) > 0 && typeof subscriptionOption === 'string') {
                                const subscriptionOptionNumber: number = parseInt(subscriptionOption) - 1;
                                const subscriptionOptionLabel = this.fruitboxSubscriptionLabels[subscriptionOptionNumber].label;
                                boxFrequency.push(subscriptionOptionLabel);
                            } else {
                                boxFrequency.push('-1');
                            }
                        }
                        dataLayer.push({
                            event: 'formSubmit',
                            trial: (fd.get('trialSubscription') ? 'true' : 'false'),
                            box_type: boxType,
                            box_count: boxCount,
                            box_weight: boxWeight,
                            box_price: boxPrice,
                            box_frequency: boxFrequency
                        });
                    } else {
                        dataLayer.push({
                            event: 'formSubmit'
                        });
                    }
                }
                if (res.data.success) {
                    this.success = true;
                } else {
                    this.errorMessage = res.data.details.errorMessage;
                }
            } else {
                this.items.forEach((item) => item.reportCustomValidity());
                for (let i = 0; i < form.elements.length; i++) {
                    // trigger custom validation messages
                    (form.elements[i] as HTMLFormElement).dataset.touched = '';
                }
            }
        } catch (err) {
            this.errorMessage = this.serverError;
        } finally {
            this.sending = false;
        }
    }

    checkItemsValidity(): boolean {
        return this.items.every((item) => item.checkCustomValidity());
    }

    toggle() {
        this.checked = !this.checked;
    }

    registerItem(item) {
        this.items.push(item);
    }
}
</script>
